import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';

import { getMillsTimestamp } from '../../helpers/getDate';
import { getUserAuthData, logout } from '../../services/users/auth.service';

export const AuthDataContext = createContext<any>(null);

const initialAuthData = {};

/**
 * @abstract Valid the expiration date from access token
 *
 */
const validAuthData = () => {
  const currentAuthData = getUserAuthData();
  const expirationDate = new Date(currentAuthData.expiration_date).getTime();
  const now = new Date().getTime();

  if (Object.keys(currentAuthData).length) {
    return expirationDate - now > 0 ? currentAuthData : null;
  }

  return null;
};

const AuthDataProvider = ({ ...props }) => {
  const [authData, setAuthData] = useState<any>(initialAuthData);
  const location = useLocation();
  const queryParams = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);

    return {
      access_token: searchParams.get('token'),
      bff_token: searchParams.get('bff_token'),
      expiration_date: searchParams.get('expiration_date'),
      user_email: searchParams.get('user_email'),
      user_phone: searchParams.get('user_phone'),
      user_first_name: searchParams.get('user_first_name'),
      user_last_name: searchParams.get('user_last_name'),
      user_identification_number: searchParams.get(
        'user_identification_number'
      ),
      policy_id: searchParams.get('policy_id'),
      product_code: searchParams.get('product_code'),
      plate_number: searchParams.get('plate_number'),
    };
  }, [location.search]);

  const {
    access_token,
    bff_token,
    expiration_date,
    user_email,
    user_phone,
    user_first_name,
    user_last_name,
    user_identification_number,
    policy_id,
    product_code,
    plate_number,
  } = queryParams;

  /* The first time the component is rendered, it tries to
   * fetch the auth data from a source, like a cookie or
   * the localStorage.
   */
  useEffect(() => {
    if (!access_token) return;

    let userObj: any = {
      access_token: access_token,
      bff_token,
      expiration_date: getMillsTimestamp(+expiration_date),
      embed: true,
      policy_id: policy_id ? +policy_id : null,
      product_code: product_code ? +product_code : null,
    };

    if (user_email)
      userObj = {
        ...userObj,
        user: {
          ...userObj.user,
          email: user_email,
        },
      };

    if (user_phone)
      userObj = {
        ...userObj,
        user: {
          ...userObj.user,
          phone: user_phone,
        },
      };

    if (user_first_name)
      userObj = {
        ...userObj,
        user: {
          ...userObj.user,
          first_name: user_first_name,
        },
      };

    if (user_last_name)
      userObj = {
        ...userObj,
        user: {
          ...userObj.user,
          last_name: user_last_name,
        },
      };

    if (user_identification_number)
      userObj = {
        ...userObj,
        user: {
          ...userObj.user,
          identification_number: user_identification_number,
        },
      };

    if (plate_number)
      userObj = {
        ...userObj,
        plate_number: plate_number,
      };

    localStorage.setItem('user', JSON.stringify(userObj));
  }, [
    access_token,
    bff_token,
    expiration_date,
    user_email,
    user_phone,
    user_first_name,
    user_last_name,
    user_identification_number,
    policy_id,
    product_code,
    plate_number,
  ]);

  useEffect(() => {
    const currentAuthData = validAuthData();
    if (currentAuthData) {
      setAuthData(currentAuthData);
    } else {
      logout();
    }
  }, []);

  const setUserData = (data: any) => {
    const allData = {
      ...authData,
      user: {
        ...authData.user,
        ...data,
      },
    };
    localStorage.setItem('user', JSON.stringify(allData));
    setAuthData(allData);
  };

  const onLogout = () => {
    logout();
    setAuthData(initialAuthData);
  };

  const onLogin = (newAuthData: any) => {
    setAuthData(newAuthData);
  };

  const authDataValue = useMemo(
    () => ({ ...authData, setUserData, onLogin, onLogout }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [authData]
  );

  return <AuthDataContext.Provider value={authDataValue} {...props} />;
};

export const useAuthDataContext = () => useContext(AuthDataContext);

export default AuthDataProvider;
